<template>
  <v-row no-gutters>
    <v-col cols="12">
      <sidebar-row>
        <v-row no-gutters class="align-baseline">
          <v-col>
            <v-subheader class="text-left text-body-1 font-weight-bold font--text pa-0">
              {{ $i18n.t('sidebar.folderContent') }}
            </v-subheader>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="auto" v-if="folderDocuments.length > 0">
            <v-tooltip bottom color="font">
              <template #activator="{ on }">
                <span v-on="on">
                  <main-loader v-if="isFolderDownloading" />
                  <v-btn icon @click="downloadFolderContent" v-else>
                    <v-icon> mdi-download </v-icon>
                  </v-btn>
                </span>
              </template>
              {{ $i18n.t('menu.downloadFolder') }}
            </v-tooltip>
          </v-col>
        </v-row>
      </sidebar-row>
      <sidebar-row :border-bottom="folderDocuments.length === 0">
        <v-row no-gutters class="mt-2" :class="folderDocuments.length === 0 ? 'mb-4' : 'mb-2'">
          <v-col cols="auto">
            <base-button
              icon="mdi-plus"
              translation-path="button.addDocument"
              class="text-h6"
              icon-size="25px"
              :disabled="!hasPermissions"
              @click="toggleAdding"
            ></base-button>
          </v-col>
        </v-row>
      </sidebar-row>
      <sidebar-row
        v-for="doc in computedData"
        :icon="doc.icon"
        :icon-color="doc.iconColor"
        :key="doc.key"
        border-bottom
      >
        <document-row
          :doc="doc"
          class="py-4"
          ref="TEST"
          :id="doc.document_id"
          @downloadDocument="downloadFile"
          @changeDocumentName="openEditNameDialog"
          @deleteDocument="openDeleteDialog"
        ></document-row>
      </sidebar-row>
    </v-col>
    <main-input-dialog
      v-if="isAddingLoaded"
      :is-visible.sync="isAddingVisible"
      agree-text="dialog.title.add"
      dialog-width="40%"
      @agree="handleDocumentAdd"
      @modalClosed="onClose"
      :inputs="inputs"
      :title-translation="this.$i18n.t('dialog.title.addDocument')"
      :loading="isSending"
    >
      <template #file.input>
        <v-row no-gutters>
          <v-col cols="12">
            <file-input
              ref="file"
              :options="dropzoneOptions"
              :show-added-files="true"
              :events="{
                'vdropzone-sending': sendingEvent,
                'vdropzone-success': onUploadSuccess,
              }"
            ></file-input>
          </v-col>
        </v-row>
      </template>
    </main-input-dialog>
    <main-dialog
      v-if="isDeleteDialogLoaded"
      :is-visible.sync="isDeleteDialogVisible"
      :title="$i18n.t('dialog.title.deleteDocument')"
      :body="$i18n.t('dialog.body.deleteDocumentConfirmation', { name: editDocName })"
      @agree="handleDeleteDocument"
      :loading="isDeleteButtonLoading"
      bodyPadding="20px"
      agree-text="dialog.agreeDelete"
    ></main-dialog>
    <main-dialog
      v-if="isEditNameDialogLoaded"
      :is-visible.sync="isEditNameDialogVisible"
      :title="$i18n.t('dialog.title.changeName')"
      @agree="handleDocumentEditName"
      :loading="isEditNameButtonLoading"
      agree-text="dialog.agreeSave"
      header-shadow
      :is-valid="isNameValid"
      body-padding="16px"
      underlineFooter
      :is-buttons-right="false"
    >
      <template #body>
        <main-input-dialog-row
          name="name"
          translation-path="dialog.name"
          :data-type="dataType"
          clearable
          cols="12"
          :value="editDocName"
          @input="editDocName = $event"
        ></main-input-dialog-row>
      </template>
    </main-dialog>
  </v-row>
</template>

<script>
import { call } from 'vuex-pathify';

import SidebarRow from '@/components/SidebarRow';
import BaseButton from '@/components/BaseButton';
import FileInput from '@/components/FileInput';
import DocumentRow from '@/components/DocumentRow';
import MainInputDialogRow from '@/components/MainInputDialogRow';
import MainLoader from '@/components/MainLoader';

import { getIconByFileType, getIconColorByFileType } from '@/assets/js/utility';

export default {
  name: 'FolderContent',
  components: {
    MainLoader,
    MainInputDialogRow,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
    DocumentRow,
    BaseButton,
    SidebarRow,
    FileInput,
  },
  props: {
    folderId: {
      type: [String, Number],
      required: true,
    },
    folderDocuments: {
      type: Array,
      default: () => [],
    },
    hasPermissions: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    inputs: [
      {
        name: 'documentName',
        translationPath: 'dialog.documentName',
        clearable: true,
        dataType: {
          name: 'text',
        },
      },
      {
        name: 'file',
        translationPath: 'dialog.file',
        subtitle: 'dialog.body.chooseFileDocument',
        align: 'start',
      },
    ],
    isAddingVisible: false,
    isAddingLoaded: false,
    isFetching: false,
    isDeleteDialogVisible: false,
    isDeleteDialogLoaded: false,
    isDeleteButtonLoading: false,
    isEditNameDialogVisible: false,
    isEditNameDialogLoaded: false,
    isEditNameButtonLoading: false,
    editDocId: undefined,
    editDocName: '',
    isSending: false,
    dataType: {
      name: 'text',
    },
    isFolderDownloading: false,
  }),
  computed: {
    isNameValid() {
      if (this.editDocName === null) {
        return false;
      } else {
        return !(this.editDocName.trim() === '' || this.editDocName === undefined);
      }
    },
    postDocumentURL() {
      return `folders/${this.folderId}/documents`;
    },
    dropzoneOptions() {
      return {
        url: `${process.env.VUE_APP_API_URL}/${this.postDocumentURL}`,
        method: 'post',
        autoProcessQueue: false,
        uploadMultiple: false,
        addRemoveLinks: true,
        paramName: 'file',
        maxFiles: 1,
        init: function() {
          this.on('maxfilesexceeded', function(file) {
            this.removeAllFiles();
            this.addFile(file);
          });
        },
      };
    },
    computedData() {
      // wcześniej jako klucz używany był atrybut id, który się nie zmieniał po zmianie nazwy dokumentu, przez co nie aktualizował się widok
      const timestamp = this.$dayjs().unix();
      return this.folderDocuments.map(item => {
        return {
          ...item,
          key: `${item.document_id}@${timestamp}`,
          subheader: this.getSubheaderText(item.document_filename, item.document_added_by_name, item.document_added_at),
          icon: getIconByFileType(item.document_filename),
          iconColor: getIconColorByFileType(item.document_filename),
        };
      });
    },
  },
  methods: {
    downloadDocument: call('layers/downloadDocument'),
    downloadFolder: call('layers/downloadFolder'),
    deleteFolderDocument: call('layers/deleteFolderDocument'),
    editDocumentName: call('layers/editDocumentName'),

    async downloadFile(id) {
      await this.downloadDocument(id);
      this.$refs.TEST.find(el => el.$attrs.id === id).finishDownload();
    },
    async downloadFolderContent() {
      this.isFolderDownloading = true;
      await this.downloadFolder(this.folderId);
      this.isFolderDownloading = false;
    },
    async handleDocumentAdd() {
      if (this.$refs.file.$refs.dropzone.getAcceptedFiles().length < 1) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.filesRequired'),
        });
      } else if (!this.inputs.find(v => v.name === 'documentName').value) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.nameRequired'),
        });
      } else {
        this.isSending = true;
        this.$refs.file.$refs.dropzone.processQueue();
      }
    },
    async handleDeleteDocument() {
      const payload = {
        document_id: this.editDocId,
        folder_id: this.folderId,
      };
      this.isDeleteButtonLoading = true;
      await this.deleteFolderDocument(payload);
      this.isDeleteButtonLoading = false;
      this.isDeleteDialogLoaded = false;
      this.isDeleteDialogVisible = false;
      this.editDocId = undefined;
      this.editDocName = '';
    },

    async handleDocumentEditName() {
      try {
        this.isEditNameButtonLoading = true;
        const payload = {
          document_id: this.editDocId,
          folder_id: this.folderId,
          body: {
            name: this.editDocName,
          },
        };
        await this.editDocumentName(payload);
        this.editDocName = '';
        this.editDocId = undefined;
        this.isEditNameButtonLoading = false;
      } catch (error) {
        console.log('handleBlockUser -> error', error);
        this.isEditNameDialogLoaded = false;
      }
      this.isEditNameDialogVisible = false;
    },

    async openEditNameDialog(payload) {
      const { id, name } = payload;
      this.editDocId = id;
      this.editDocName = name;
      this.isEditNameDialogLoaded = true;
      this.isEditNameDialogVisible = true;
    },
    openDeleteDialog(payload) {
      const { id, name } = payload;
      this.editDocId = id;
      this.editDocName = name;
      this.isDeleteDialogLoaded = true;
      this.isDeleteDialogVisible = true;
    },
    getSubheaderText(filename, owner, date) {
      return `${filename}, ${owner}, ${this.$dayjs(date).format('L')}`;
    },
    onUploadSuccess() {
      this.$emit('documentUploaded');
      this.onClose();
    },
    sendingEvent(files, xhr, formData) {
      formData.append('document_name', this.inputs.find(v => v.name === 'documentName').value);
    },
    onClose() {
      this.$refs.file.$refs.dropzone.removeAllFiles();
      this.isSending = false;
      this.inputs.find(v => v.name === 'documentName').value = '';
      this.isAddingVisible = false;
    },
    toggleAdding() {
      this.isAddingLoaded = true;
      this.isAddingVisible = true;
    },
  },
};
</script>

<style scoped lang="scss"></style>
